import * as APITypes from "~/API";
import { utcCalendarDate } from "./stayCalendar";

export type StayOpsFilter = "pending" | "arrivals" | "departures" | "noshow";

export type StayOpsFlags = {
  pending: boolean;
  arrival: boolean;
  departure: boolean;
  noshow: boolean;
};

function utcDay(iso: string | null | undefined): string | null {
  if (!iso?.trim()) return null;
  const ms = Date.parse(iso.trim());
  if (Number.isNaN(ms)) return null;
  return utcCalendarDate(ms);
}

export function stayOpsFlags(
  stay: Pick<APITypes.Stay, "status" | "checkIn" | "checkOut">,
  todayUtc: string,
): StayOpsFlags {
  const checkInDay = utcDay(stay.checkIn);
  const checkOutDay = utcDay(stay.checkOut);
  const pending = stay.status === APITypes.StayStatusType.Pending;
  const confirmed = stay.status === APITypes.StayStatusType.Confirmed;
  const checkedIn = stay.status === APITypes.StayStatusType.CheckedIn;
  return {
    pending,
    arrival: confirmed && checkInDay === todayUtc,
    departure: (confirmed || checkedIn) && checkOutDay === todayUtc,
    noshow: confirmed && !!checkInDay && checkInDay < todayUtc,
  };
}

export function stayMatchesOpsFilter(
  stay: Pick<APITypes.Stay, "status" | "checkIn" | "checkOut">,
  filter: StayOpsFilter | "all",
  todayUtc: string,
): boolean {
  if (filter === "all") return true;
  const flags = stayOpsFlags(stay, todayUtc);
  switch (filter) {
    case "pending":
      return flags.pending;
    case "arrivals":
      return flags.arrival;
    case "departures":
      return flags.departure;
    case "noshow":
      return flags.noshow;
    default:
      return true;
  }
}

/** Lower rank = more urgent / currently active. */
const STAY_TABLE_STATUS_RANK: Record<string, number> = {
  CheckedIn: 0,
  Confirmed: 1,
  Pending: 2,
  Inquiry: 3,
  Completed: 4,
  Cancelled: 5,
  Rejected: 6,
};

export function stayTableStatusRank(status: string): number {
  return STAY_TABLE_STATUS_RANK[status] ?? 99;
}

type StayTableSortStay = Pick<APITypes.Stay, "status" | "checkIn" | "checkOut">;

/**
 * Active stays first (in-house, then upcoming, then pending).
 * Within a status, soonest checkout/arrival is first; history is newest first.
 */
export function compareStayTableRows(
  a: { stay: StayTableSortStay },
  b: { stay: StayTableSortStay },
): number {
  const rankA = stayTableStatusRank(a.stay.status);
  const rankB = stayTableStatusRank(b.stay.status);
  if (rankA !== rankB) return rankA - rankB;

  const inA = Date.parse(a.stay.checkIn) || 0;
  const inB = Date.parse(b.stay.checkIn) || 0;
  if (rankA === 0) {
    const outA = Date.parse(a.stay.checkOut) || 0;
    const outB = Date.parse(b.stay.checkOut) || 0;
    if (outA !== outB) return outA - outB;
    return inA - inB;
  }
  if (rankA <= 3) return inA - inB;
  return inB - inA;
}
